import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { StatisticsPage } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type { MonTableCollection } from '@client/ng2/models/table-collection.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

const EXIT_PAGES_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.exitPages.header',
    pageSubheaderKey: 'siteAnalytics.exitPages.subheader',
    tableKey: 'site-analytics-exit-pages',
    dataSource: 'exitPages',
    exportCategory: 'entry_exit_exit',
    searchPlaceholderKey: 'siteAnalytics.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.scaffold.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.scaffold.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.exitPages.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: 'visits',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    chart: {
        titleKey: 'siteAnalytics.exitPages.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    columns: [
        {
            id: 'page',
            headerKey: 'siteAnalytics.scaffold.table.column.url',
            displayType: 'link',
            width: 'xl',
            ariaLabelKey: 'siteAnalytics.scaffold.table.urlAriaLabel{{url}}',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            sortable: true,
            sortField: 'visits',
            width: 'md',
        },
        {
            id: 'visitsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.visitsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'exits',
            headerKey: 'siteAnalytics.scaffold.table.column.exits',
            displayType: 'text',
            sortable: true,
            sortField: 'exit_visits',
            width: 'md',
        },
        {
            id: 'exitsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.exitsComparison',
            displayType: 'text',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            sortable: false,
            sortField: 'avg_time',
            width: 'md',
        },
        {
            id: 'avgTimeComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTimeComparison',
            displayType: 'duration',
            visibility: 'comparison',
            width: 'md',
        },
    ],
};

const loadExitPagesData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;
    const isComparisonMode = Boolean(request.comparisonDateRange);

    const currentParams = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        sortBy: request.sortBy,
        sortDirection: request.sortDir,
        search: request.search,
    });

    if (!isComparisonMode || !request.comparisonDateRange) {
        const currentExitPagesResponse = await statsRepoService.getExitPages(
            currentParams,
            contextTokens.NO_PARSE_SEARCH,
        );

        const normalizedCurrentRows = normalizeRows(currentExitPagesResponse);

        return {
            rows: normalizedCurrentRows,
            chart: {
                labels: normalizedCurrentRows.map((row) => getPageLabel(row)),
                primaryValues: normalizedCurrentRows.map((row) => toInteger(row.visits)),
            },
            summary: null,
            total: getTotal(currentExitPagesResponse, normalizedCurrentRows.length),
            perPage: getPerPage(currentExitPagesResponse, request.perPage),
            page: getCurrentPage(currentExitPagesResponse, request.page),
        };
    }

    const comparisonParams = statisticsParamsService.buildComparisonParams(
        currentParams,
        request.comparisonDateRange,
        STATISTICS.DATE_FORMAT,
    );

    const [currentExitPagesComparisonResponse, comparisonExitPagesResponse] = await Promise.all([
        statsRepoService.getExitPages(currentParams, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getExitPages(comparisonParams, contextTokens.NO_PARSE_SEARCH),
    ]);

    const normalizedComparisonRows = normalizeRows(
        currentExitPagesComparisonResponse,
        comparisonExitPagesResponse,
    );

    return {
        rows: normalizedComparisonRows,
        chart: {
            labels: normalizedComparisonRows.map((row) => getPageLabel(row)),
            primaryValues: normalizedComparisonRows.map((row) => toInteger(row.visits)),
            comparisonValues: normalizedComparisonRows.map((row) => toInteger(row.visitsComparison)),
        },
        summary: null,
        total: getTotal(currentExitPagesComparisonResponse, normalizedComparisonRows.length),
        perPage: getPerPage(currentExitPagesComparisonResponse, request.perPage),
        page: getCurrentPage(currentExitPagesComparisonResponse, request.page),
    };
};

function normalizeRows (
    currentExitPages: MonTableCollection<StatisticsPage>,
    comparisonExitPages?: MonTableCollection<StatisticsPage>,
): SiteAnalyticsTableRow[] {
    const comparisonByKey = new Map<string, StatisticsPage>();

    if (comparisonExitPages) {
        for (const page of comparisonExitPages) {
            const pageKey = getComparisonKey(page);
            if (pageKey && !comparisonByKey.has(pageKey)) {
                comparisonByKey.set(pageKey, page);
            }
        }
    }

    return currentExitPages.map((page, index) => {
        const pageKey = getComparisonKey(page);
        const comparisonPage = pageKey ? comparisonByKey.get(pageKey) : undefined;
        const url = String(page.url || '');
        const title = String(page.title || '');

        const exits = toInteger(page.exit_visits);
        const visits = toInteger(page.visits);

        const comparisonExits = comparisonPage ? toInteger(comparisonPage.exit_visits) : null;
        const comparisonVisits = comparisonPage ? toInteger(comparisonPage.visits) : null;

        return {
            id: `${url || pageKey || 'row'}-${index}`,
            page: {
                label: url,
                href: url,
                ...(title && title !== url ? { tooltip: title } : {}),
            },
            visits,
            visitsComparison: comparisonVisits,
            exits: formatCountWithPercent(exits, visits > 0 ? (exits / visits) * 100 : 0),
            exitsComparison: comparisonExits === null || comparisonVisits === null
                ? null
                : formatCountWithPercent(comparisonExits, comparisonVisits > 0 ? (comparisonExits / comparisonVisits) * 100 : 0),
            avgTime: toInteger(page.avg_time),
            avgTimeComparison: comparisonPage ? toInteger(comparisonPage.avg_time) : null,
        };
    });
}

function getPageLabel (row: SiteAnalyticsTableRow): string {
    const linkCell = toLinkCell(row.page);
    if (linkCell) {
        return getUrlPath(linkCell.label);
    }

    if (typeof row.page === 'string') {
        return getUrlPath(row.page);
    }

    return '';
}

function getUrlPath (value: string): string {
    try {
        const url = new URL(value);

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return value;
    }
}

function getComparisonKey (page: StatisticsPage): string {
    return String(page.url || page.label || page.title || '').trim();
}

function formatCountWithPercent (count: number, percent: number): string {
    return `${formatInteger(count)} (${formatPercentage(percent)})`;
}

function formatInteger (value: number): string {
    return value.toLocaleString();
}

function formatPercentage (value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${safeValue.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function toLinkCell (value: unknown): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const link = value as { label?: unknown; href?: unknown; tooltip?: unknown };
    if (typeof link.label !== 'string' || typeof link.href !== 'string') {
        return null;
    }

    return {
        label: link.label,
        href: link.href,
        ...(typeof link.tooltip === 'string' && link.tooltip ? { tooltip: link.tooltip } : {}),
    };
}

function toInteger (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

export const EXIT_PAGES_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'exitPages',
    config: EXIT_PAGES_CONFIG,
    loadData: loadExitPagesData,
};

export const EXIT_PAGES_PAGE_CONFIG = EXIT_PAGES_DEFINITION.config;
